import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import {
  ScrollText, RefreshCw, Filter, User, Settings, Trash2, RotateCcw, PowerOff, Edit, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";

const ACTION_META = {
  "user.update":        { label: "Profil-Update",   icon: Edit,     color: "text-sky-300",    bg: "bg-sky-500/10 border-sky-500/30" },
  "user.soft_delete":   { label: "Deaktiviert",     icon: PowerOff, color: "text-amber-300",  bg: "bg-amber-500/10 border-amber-500/30" },
  "user.restore":       { label: "Wiederhergestellt", icon: RotateCcw, color: "text-[#00E575]", bg: "bg-[#00E575]/10 border-[#00E575]/30" },
  "user.hard_delete":   { label: "Endgültig gelöscht", icon: Trash2, color: "text-red-300",   bg: "bg-red-500/10 border-red-500/30" },
  "admin.setting_change": { label: "Setting geändert", icon: Settings, color: "text-violet-300", bg: "bg-violet-500/10 border-violet-500/30" },
};

const ACTION_OPTIONS = [
  { value: "", label: "Alle Aktionen" },
  { value: "user.update", label: "Profil-Updates" },
  { value: "user.soft_delete", label: "Deaktivierungen" },
  { value: "user.restore", label: "Wiederherstellungen" },
  { value: "user.hard_delete", label: "Endgültige Löschungen" },
  { value: "admin.setting_change", label: "Einstellungs-Änderungen" },
];

const PAGE_SIZE = 50;

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (action) params.set("action", action);
      const r = await api.get(`/admin/audit-log?${params}`);
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit-Log konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [action, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Audit-Log" back="/admin/users" />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="h-12 w-12 rounded-2xl bg-violet-500/15 flex items-center justify-center">
            <ScrollText className="text-violet-300" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-display text-2xl font-semibold">Audit-Log</h1>
            <p className="text-sm text-zinc-300 font-medium">Wer hat wann was geändert — chronologisch, neueste zuerst.</p>
          </div>
          <button
            data-testid="audit-log-refresh-btn"
            onClick={() => { setPage(0); load(); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs font-semibold hover:bg-zinc-700 transition"
          >
            <RefreshCw size={13} /> Aktualisieren
          </button>
        </div>

        {/* Filter row */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-zinc-400" />
          <span className="text-xs uppercase tracking-widest text-zinc-400">Filter:</span>
          <select
            data-testid="audit-log-action-filter"
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(0); }}
            className="h-9 bg-[#18181B] border border-zinc-700 rounded-xl px-3 text-sm text-zinc-100"
          >
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="ml-auto text-sm text-zinc-300 font-medium">
            <span data-testid="audit-log-total" className="font-mono text-zinc-200">{total}</span> Einträge
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400">
            <Loader2 className="animate-spin mr-2" size={18} /> Lade…
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center text-zinc-400">
            Keine Audit-Einträge gefunden.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="audit-log-list">
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, icon: ScrollText, color: "text-zinc-300", bg: "bg-zinc-800/40 border-zinc-700" };
              const Icon = meta.icon;
              const isOpen = expanded === e.id;
              const hasMeta = e.metadata && Object.keys(e.metadata).length > 0;
              return (
                <li
                  key={e.id}
                  data-testid={`audit-entry-${e.id}`}
                  className={`border rounded-2xl px-4 py-3 ${meta.bg}`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${meta.color} bg-[#09090B]/60 border border-zinc-800`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold uppercase tracking-wider break-all min-w-0 ${meta.color}`}>{meta.label}</span>
                        <span className="text-xs text-zinc-400 font-medium">·</span>
                        <span className="text-xs text-zinc-300 font-mono">{fmtTs(e.ts)}</span>
                      </div>
                      <div className="mt-1 text-sm text-zinc-100">
                        <strong className="text-zinc-50">{e.actor_name || "—"}</strong>
                        <span className="text-zinc-400"> ({e.actor_role || "?"}) </span>
                        {e.target_name && (
                          <>
                            <span className="text-zinc-400">→ </span>
                            <span className="inline-flex items-center gap-1 text-zinc-100">
                              <User size={11} className="text-zinc-400" />
                              {e.target_name}
                            </span>
                            {e.target_email && (
                              <span className="text-xs text-zinc-300 font-medium"> · {e.target_email}</span>
                            )}
                          </>
                        )}
                        {!e.target_name && e.target_id && (
                          <>
                            <span className="text-zinc-400">→ </span>
                            <span className="font-mono text-xs text-zinc-300">{e.target_id}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {hasMeta && (
                      <button
                        data-testid={`audit-entry-toggle-${e.id}`}
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                        className="h-8 px-2 rounded-lg text-xs text-zinc-300 hover:bg-zinc-800 inline-flex items-center gap-1 shrink-0"
                      >
                        Details {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                  {hasMeta && isOpen && (
                    <pre
                      data-testid={`audit-entry-meta-${e.id}`}
                      className="mt-3 p-3 rounded-lg bg-[#09090B] border border-zinc-800 text-xs text-zinc-200 font-mono overflow-x-auto leading-relaxed"
                    >
{JSON.stringify(e.metadata, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              data-testid="audit-log-prev-page"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="h-9 px-3 rounded-lg bg-zinc-800 text-zinc-200 text-sm disabled:opacity-40 hover:bg-zinc-700"
            >
              ← Vorherige
            </button>
            <span className="text-sm text-zinc-300 font-medium">
              Seite <span className="font-mono text-zinc-100">{page + 1}</span> / {totalPages}
            </span>
            <button
              data-testid="audit-log-next-page"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="h-9 px-3 rounded-lg bg-zinc-800 text-zinc-200 text-sm disabled:opacity-40 hover:bg-zinc-700"
            >
              Nächste →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
